import DOMPurify from 'dompurify';
import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Edit2, Trash, SaveAdd, ArchiveTick, Tick, Spinner, Copy } from '@/components/icons';
import SpellcheckSuggestionPopover from '@/components/spellcheck/SpellcheckSuggestionPopover';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { APP_FONT } from '@/constants';
import { useSpellcheckEnabled } from '@/context/SpellcheckContext';
import { useDebouncedSpellcheck } from '@/hooks/use-debounced-spellcheck';
import { useHoverIntent } from '@/hooks/use-hover-intent';
import { useOutsideSave } from '@/hooks/use-outside-save';
import { isTextSelected } from '@/lib/is-text-selected';
import { getSectionTextOffsetFromPoint } from '@/lib/report-caret';
import {
    copyRichTextToClipboard,
    keepOnlyFontSizeStyles,
    normalizeRichTextValue,
    stripTrailingEmptyParagraphs,
} from '@/lib/rich-text';
import {
    decorateHtmlWithSpellcheckIssues,
    getHtmlTextForSpellcheck,
    replaceHtmlSpellcheckRange,
} from '@/lib/spellcheck';
import { cn } from '@/lib/utils';
import { REPORT_CONTENT_CLASSNAME } from './constants';
import FloatingSectionEditor from './FloatingSectionEditor';
import { SectionContextMenu } from './SectionContextMenu';
import type { AIImprovementIntent } from '@/api/ai';
import type { PaidEntitlementPlan } from '@/api/entitlements';

type AvailablePlan = {
    id: PaidEntitlementPlan;
    name: string;
};

const SPELLCHECK_VIEWPORT_ROOT_MARGIN = '700px 0px';

interface EditableSectionProps {
    content?: string;
    defaultEditValue?: string;
    copyContent?: string;
    onSave: (value: string) => void;
    onDelete?: () => void;
    placeholder?: string;
    disabled?: boolean;
    isEditing?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
    isDefault?: boolean;
    onSaveAsDefault?: () => void;
    onBlockedSaveAsDefault?: () => void;
    isSaveAsDefaultBlocked?: boolean;
    saveAsDefaultBlockedMessage?: string;
    saveAsDefaultAvailablePlan?: AvailablePlan | null;
    isUpdatingDefault?: boolean;
    reportId?: string;
    sectionId?: string;
    /**
     * Identity for click-to-caret offset math (`data-editable-section-anchor`).
     * Must be unique to this edited unit — fragments sharing an anchor have
     * their text summed as earlier pages of the same content. Defaults to
     * `sectionId`; tests blocks pass a per-block value since they all share
     * sectionId "tests".
     */
    anchorId?: string;
    /**
     * Blur/fade the read-only text because this section's rich text editor is
     * open. Defaults to `isEditing`; sections split across pages pass `true` on
     * every fragment so the continuation page recedes alongside the fragment
     * that owns the editor.
     */
    isContentDimmed?: boolean;
    onBeforeGenerate?: () => Promise<void>;
    /** Opens the comment dialog targeted at this section. Absent = item hidden. */
    onComment?: () => void;
    isAIBlocked?: boolean;
    aiAvailablePlan?: AvailablePlan | null;
}

function EditableSectionComponent({
    content,
    defaultEditValue,
    copyContent,
    onSave,
    onDelete,
    placeholder = 'Clique para editar...',
    disabled = false,
    isEditing,
    onEditingChange,
    isDefault,
    onSaveAsDefault,
    onBlockedSaveAsDefault,
    isSaveAsDefaultBlocked,
    saveAsDefaultBlockedMessage,
    saveAsDefaultAvailablePlan,
    isUpdatingDefault,
    reportId,
    sectionId,
    anchorId,
    isContentDimmed,
    onBeforeGenerate,
    onComment,
    isAIBlocked,
    aiAvailablePlan,
}: EditableSectionProps) {
    const [editValue, setEditValue] = useState(defaultEditValue ?? content ?? '');
    const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [initialCaret, setInitialCaret] = useState<{ textOffset: number } | null>(null);
    const [pendingAIIntent, setPendingAIIntent] = useState<AIImprovementIntent | null>(null);
    const [copied, setCopied] = useState(false);
    const [hasEnteredSpellcheckViewport, setHasEnteredSpellcheckViewport] = useState(false);
    const [activeSpellcheckIssue, setActiveSpellcheckIssue] = useState<{
        issueIndex: number;
        anchorPoint: {
            clientX: number;
            clientY: number;
            pageX: number;
            pageY: number;
        };
    } | null>(null);
    const [toolbarPlacement, setToolbarPlacement] = useState<'top' | 'bottom'>('top');
    const hasContent = !!content;
    const resolvedAnchorId = anchorId ?? sectionId;
    // While the floating editor is open the same paragraphs are on screen
    // twice; push the read-only copy back so the editor is the sharp one. The
    // filter lives on the text itself, never on the wrapper: it would create a
    // containing block for the hover toolbar and the spellcheck popover, and
    // blur the "being edited" ring along with them.
    const shouldDimContent = (isContentDimmed ?? isEditing) && !disabled;
    const dimClassName = cn(
        'transition-[filter,opacity] duration-200 ease-out',
        shouldDimContent ? 'opacity-60 blur-[2px]' : 'opacity-100 blur-0',
    );
    const sectionAnchorRef = useRef<HTMLDivElement | null>(null);
    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const editorContainerRef = useRef<HTMLDivElement | null>(null);
    const aiPanelRef = useRef<HTMLDivElement | null>(null);
    const copyTimeoutRef = useRef<number | null>(null);
    const pendingCaretRef = useRef<{ textOffset: number } | null>(null);
    const isSpellcheckEnabled = useSpellcheckEnabled();

    // The toolbar waits for hover intent before appearing (a cursor crossing the
    // page must not light up every block it passes) and lingers on the way out:
    // the button stack overflows below short one-line blocks, so travelling the
    // cursor onto it crosses a brief dead zone the grace period bridges.
    const {
        isActive: toolbarVisible,
        hoverProps: toolbarHoverProps,
        showNow: showToolbarNow,
        hide: hideToolbar,
    } = useHoverIntent({ disabled });

    // Flip the toolbar to grow upward when the downward stack would spill past
    // the page-content box, whose `overflow-hidden` would otherwise clip the
    // bottom-most (Remover) button on sections near the footer. All
    // measurements use getBoundingClientRect (scaled space) so the decision
    // stays correct under report zoom, and it runs before paint so the stack
    // never flashes in the wrong place.
    useLayoutEffect(() => {
        if (!toolbarVisible) return;
        const section = sectionAnchorRef.current;
        const clip = section?.closest('[data-page-content]');
        if (!section || !clip) return;
        const sectionRect = section.getBoundingClientRect();
        const clipRect = clip.getBoundingClientRect();
        const toolbarH = toolbarRef.current?.getBoundingClientRect().height ?? 0;
        setToolbarPlacement(sectionRect.top + toolbarH > clipRect.bottom ? 'bottom' : 'top');
    }, [toolbarVisible]);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) {
                window.clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setEditValue(defaultEditValue ?? content ?? '');
    }, [content, defaultEditValue]);

    useEffect(() => {
        if (!isEditing) {
            setIsExpanded(false);
            setInitialCaret(null);
            setPendingAIIntent(null);
        }
    }, [isEditing]);

    useOutsideSave({
        isActive: !!isEditing,
        disabled: disabled || isAIPanelOpen || isExpanded,
        containerRef: editorContainerRef,
        portalRefs: [aiPanelRef],
        currentValue: (defaultEditValue !== undefined ? defaultEditValue : content) || '',
        editedValue: editValue,
        onSave,
        onClose: () => onEditingChange?.(false),
        normalize: normalizeRichTextValue,
    });

    const handleSave = () => {
        const normalizedValue = normalizeRichTextValue(editValue);

        onSave(normalizedValue);
        onEditingChange?.(false);
    };

    const handleCancel = () => {
        setEditValue(defaultEditValue ?? content ?? '');
        onEditingChange?.(false);
    };

    const resolveCaretFromPoint = (clientX: number, clientY: number) => {
        if (!contentRef.current || !resolvedAnchorId) return null;

        const textOffset = getSectionTextOffsetFromPoint(
            contentRef.current,
            resolvedAnchorId,
            clientX,
            clientY,
        );

        return textOffset === null ? null : { textOffset };
    };

    const copySectionContent = async () => {
        const sourceContent = copyContent ?? content;

        if (!sourceContent) return;

        try {
            await copyRichTextToClipboard(sourceContent);
            setCopied(true);
        } catch (error) {
            console.error('Failed to copy section content', error);
            return;
        }

        if (copyTimeoutRef.current) {
            window.clearTimeout(copyTimeoutRef.current);
        }
        copyTimeoutRef.current = window.setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        void copySectionContent();
    };

    const openEditorAtPendingCaret = () => {
        setInitialCaret(pendingCaretRef.current);
        onEditingChange?.(true);
    };

    const handleSelectAIPreset = (intent: AIImprovementIntent) => {
        setInitialCaret(null);
        setPendingAIIntent(intent);
        onEditingChange?.(true);
    };

    const sanitizedContent = useMemo(
        // FORBID_TAGS drops the orphaned checkboxes left in reports written
        // while the editor still offered checklists; the <ul> renders as a
        // plain bullet list, which is what the DOCX export always made of it.
        () =>
            stripTrailingEmptyParagraphs(
                keepOnlyFontSizeStyles(
                    DOMPurify.sanitize(content || '', { FORBID_TAGS: ['input'] }),
                ),
            ),
        [content],
    );
    const highlightedContent = useMemo(
        () =>
            sanitizedContent
                .replace(
                    /{PREENCHA_AQUI}/g,
                    '<span class="bg-blue-100 px-1 rounded">{PREENCHA_AQUI}</span>',
                )
                .replace(
                    /{QUANTIDADE_DE_SESSOES}/g,
                    '<span class="bg-amber-100 px-1 rounded">{QUANTIDADE_DE_SESSOES}</span>',
                ),
        [sanitizedContent],
    );
    const canRunSpellcheck =
        !disabled &&
        !isEditing &&
        !!content &&
        (defaultEditValue === undefined || defaultEditValue === content);

    useEffect(() => {
        if (!canRunSpellcheck || !isSpellcheckEnabled) {
            setHasEnteredSpellcheckViewport(false);
            return;
        }

        if (hasEnteredSpellcheckViewport) return;

        const element = sectionAnchorRef.current;

        if (!element || typeof IntersectionObserver === 'undefined') {
            setHasEnteredSpellcheckViewport(true);
            return;
        }

        const scrollRoot = element.closest('[data-preview-container]');
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;

                setHasEnteredSpellcheckViewport(true);
                observer.disconnect();
            },
            {
                root: scrollRoot,
                rootMargin: SPELLCHECK_VIEWPORT_ROOT_MARGIN,
                threshold: 0,
            },
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [canRunSpellcheck, hasEnteredSpellcheckViewport, isSpellcheckEnabled]);

    const isSpellcheckActive =
        canRunSpellcheck && isSpellcheckEnabled && hasEnteredSpellcheckViewport;
    const spellcheckText = useMemo(
        () => (isSpellcheckActive ? getHtmlTextForSpellcheck(highlightedContent) : ''),
        [isSpellcheckActive, highlightedContent],
    );
    const { issues: spellcheckIssues, applyLocalCorrection: applyLocalSpellcheckCorrection } =
        useDebouncedSpellcheck({
            enabled: isSpellcheckActive,
            text: spellcheckText,
            reportId,
            sectionId,
        });
    const contentWithSpellcheck = useMemo(
        () =>
            isSpellcheckActive
                ? decorateHtmlWithSpellcheckIssues(highlightedContent, spellcheckIssues)
                : highlightedContent,
        [isSpellcheckActive, highlightedContent, spellcheckIssues],
    );

    useEffect(() => {
        setActiveSpellcheckIssue(null);
    }, [spellcheckIssues]);

    const handleSpellcheckClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = (event.target as HTMLElement).closest<HTMLElement>(
            '[data-spellcheck-issue-index]',
        );

        if (!target || !event.currentTarget.contains(target)) return;

        const issueIndex = Number(target.dataset.spellcheckIssueIndex);

        if (!Number.isInteger(issueIndex) || !spellcheckIssues[issueIndex]) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setActiveSpellcheckIssue({
            issueIndex,
            anchorPoint: {
                clientX: event.clientX,
                clientY: event.clientY,
                pageX: event.pageX,
                pageY: event.pageY,
            },
        });
    };

    const handleApplySpellcheckSuggestion = (suggestion: string) => {
        if (!activeSpellcheckIssue) return;

        const issue = spellcheckIssues[activeSpellcheckIssue.issueIndex];

        if (!issue) return;

        const nextContent = normalizeRichTextValue(
            replaceHtmlSpellcheckRange(sanitizedContent, issue, suggestion),
        );
        const nextSpellcheckText = `${spellcheckText.slice(
            0,
            issue.start,
        )}${suggestion}${spellcheckText.slice(issue.end)}`;

        applyLocalSpellcheckCorrection(issue, suggestion, nextSpellcheckText);
        setEditValue(nextContent);
        onSave(nextContent);
        setActiveSpellcheckIssue(null);
    };

    return (
        <>
            <SectionContextMenu
                disabled={!!disabled}
                hasContent={hasContent}
                canUseAI={!!reportId && !!sectionId}
                isAIBlocked={isAIBlocked}
                aiAvailablePlan={aiAvailablePlan}
                canComment={!!onComment}
                canSaveAsDefault={!!onSaveAsDefault}
                isDefault={isDefault}
                isUpdatingDefault={isUpdatingDefault}
                canClear={!!onDelete}
                onEditHere={openEditorAtPendingCaret}
                onSelectAIPreset={handleSelectAIPreset}
                onCopy={() => void copySectionContent()}
                onComment={() => onComment?.()}
                onSaveAsDefault={() => {
                    if (isSaveAsDefaultBlocked) {
                        onBlockedSaveAsDefault?.();
                        return;
                    }
                    onSaveAsDefault?.();
                }}
                onClear={() => onDelete?.()}
            >
                <div
                    ref={sectionAnchorRef}
                    data-editable-section-anchor={resolvedAnchorId}
                    {...toolbarHoverProps}
                    // Tabbing into the button stack has to reveal it too — it is
                    // `pointer-events-none opacity-0` until then.
                    onFocusCapture={showToolbarNow}
                    onBlurCapture={hideToolbar}
                    // Resolve the caret here rather than in the menu item's
                    // onSelect: Radix opens the menu *at* the pointer, and
                    // `getSectionTextOffsetFromPoint` hit-tests the document —
                    // by then the topmost element at that point is the menu.
                    onContextMenu={(e) => {
                        pendingCaretRef.current = resolveCaretFromPoint(e.clientX, e.clientY);
                        // Radix's trigger preventDefaults but never stops
                        // propagation, so without this the page-level menu
                        // wrapping .report-paper would open stacked on top of
                        // this one. Its own handler still runs — stopPropagation
                        // does not set defaultPrevented, which is all
                        // composeEventHandlers checks.
                        e.stopPropagation();
                    }}
                    onClick={(e) => {
                        if (disabled || isEditing) return;
                        // On macOS Ctrl+click raises contextmenu and can still
                        // deliver a click; without this the floating editor
                        // would open underneath the menu.
                        if (e.button === 2 || e.ctrlKey) return;
                        if (isTextSelected(e.currentTarget)) return;
                        e.preventDefault();
                        e.stopPropagation();
                        setInitialCaret(resolveCaretFromPoint(e.clientX, e.clientY));
                        onEditingChange?.(true);
                    }}
                    className={cn(
                        'group relative -m-2 rounded p-2 pr-8 text-sm leading-relaxed text-slate-700 transition-colors',
                        disabled ? '' : 'cursor-text hover:bg-blue-50',
                        isEditing &&
                            !disabled &&
                            'bg-blue-50/70 ring-1 ring-blue-200 ring-offset-2 ring-offset-white',
                    )}
                >
                    {content ? (
                        <div
                            ref={contentRef}
                            data-report-content
                            className={cn(REPORT_CONTENT_CLASSNAME, dimClassName)}
                            onClick={handleSpellcheckClick}
                            dangerouslySetInnerHTML={{ __html: contentWithSpellcheck }}
                        />
                    ) : (
                        <span className={cn('italic text-slate-400', dimClassName)}>
                            {placeholder}
                        </span>
                    )}
                    {activeSpellcheckIssue &&
                        spellcheckIssues[activeSpellcheckIssue.issueIndex] && (
                            <SpellcheckSuggestionPopover
                                issue={spellcheckIssues[activeSpellcheckIssue.issueIndex]}
                                anchorPoint={activeSpellcheckIssue.anchorPoint}
                                onApply={handleApplySpellcheckSuggestion}
                                onClose={() => setActiveSpellcheckIssue(null)}
                            />
                        )}
                    {!disabled && hasContent && !isEditing && (
                        <div
                            ref={toolbarRef}
                            className={cn(
                                'absolute right-0 z-20 flex flex-col gap-1 rounded-md border border-slate-200 bg-white p-1 shadow-md transition-all duration-200',
                                toolbarPlacement === 'bottom' ? 'bottom-0' : 'top-0',
                                toolbarVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
                            )}
                            style={{ fontFamily: APP_FONT }}
                        >
                            <TooltipIconButton
                                label={copied ? 'Copiado!' : 'Copiar'}
                                side="left"
                                delayDuration={0}
                                onClick={handleCopy}
                                className={cn(
                                    '[&_svg]:size-3.5',
                                    copied
                                        ? 'bg-green-50 text-green-600'
                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                                )}
                            >
                                {copied ? <Tick /> : <Copy />}
                            </TooltipIconButton>

                            <TooltipIconButton
                                label="Editar"
                                side="left"
                                delayDuration={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditingChange?.(true);
                                }}
                                className="text-slate-600 hover:bg-slate-50 hover:text-slate-900 [&_svg]:size-3.5"
                            >
                                <Edit2 />
                            </TooltipIconButton>

                            {onSaveAsDefault && (
                                <TooltipIconButton
                                    label={
                                        isUpdatingDefault
                                            ? 'Salvando...'
                                            : isDefault
                                              ? 'É o padrão'
                                              : isSaveAsDefaultBlocked
                                                ? saveAsDefaultBlockedMessage ||
                                                  'Recurso indisponível'
                                                : 'Usar este texto como padrão'
                                    }
                                    tooltip={
                                        isUpdatingDefault ? (
                                            'Salvando...'
                                        ) : isDefault ? (
                                            'É o padrão'
                                        ) : isSaveAsDefaultBlocked ? (
                                            <div className="flex items-center gap-2">
                                                <span>
                                                    {saveAsDefaultBlockedMessage ||
                                                        'Recurso indisponível'}
                                                </span>
                                                {saveAsDefaultAvailablePlan ? (
                                                    <FeatureUnavailableChip
                                                        availablePlan={saveAsDefaultAvailablePlan}
                                                        side="left"
                                                    />
                                                ) : null}
                                            </div>
                                        ) : (
                                            'Usar este texto como padrão'
                                        )
                                    }
                                    side="left"
                                    delayDuration={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isSaveAsDefaultBlocked) {
                                            onBlockedSaveAsDefault?.();
                                            return;
                                        }

                                        if (!isDefault && !isUpdatingDefault) onSaveAsDefault();
                                    }}
                                    disabled={isDefault || isUpdatingDefault}
                                    aria-disabled={
                                        isDefault || isUpdatingDefault || isSaveAsDefaultBlocked
                                    }
                                    className={cn(
                                        '[&_svg]:size-3.5',
                                        isDefault
                                            ? 'cursor-default bg-green-50 text-green-600'
                                            : isSaveAsDefaultBlocked
                                              ? 'cursor-not-allowed text-slate-400 hover:bg-slate-50'
                                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                                    )}
                                >
                                    {isUpdatingDefault ? (
                                        <Spinner className="animate-spin" />
                                    ) : isDefault ? (
                                        <ArchiveTick />
                                    ) : (
                                        <SaveAdd />
                                    )}
                                </TooltipIconButton>
                            )}

                            {onDelete && (
                                <TooltipIconButton
                                    label="Remover"
                                    variant="ghostDestructive"
                                    side="left"
                                    delayDuration={0}
                                    className="[&_svg]:size-3.5"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete();
                                    }}
                                >
                                    <Trash />
                                </TooltipIconButton>
                            )}
                        </div>
                    )}
                </div>
            </SectionContextMenu>

            {isEditing && !disabled && (
                <FloatingSectionEditor
                    anchorRef={sectionAnchorRef}
                    editorRef={editorContainerRef}
                    aiPanelRef={aiPanelRef}
                    value={editValue}
                    onChange={setEditValue}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isAIPanelOpen={isAIPanelOpen}
                    onAIPanelOpenChange={setIsAIPanelOpen}
                    isExpanded={isExpanded}
                    onExpandedChange={setIsExpanded}
                    reportId={reportId}
                    sectionId={sectionId}
                    onBeforeGenerate={onBeforeGenerate}
                    initialCaret={initialCaret}
                    initialAIIntent={pendingAIIntent}
                />
            )}
        </>
    );
}

const EditableSection = memo(EditableSectionComponent);

export default EditableSection;
